import type { MbcBriefAssetTarget } from "@nlc/shared-types";

import { Camera, PenTool, Upload } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/button";
import { Dialog, DialogFooter } from "@/components/dialog";
import { Modal } from "@/components/modal";

import type { BriefMemory } from "../../hooks/useBriefMemory";
import type { AssetGroup, AssetGroupChange } from "./types";

import { patchBrief } from "../../lib/brief-api";
import { requestIdFromSessionId } from "../../lib/session-ids";
import { AssetSection } from "./AssetSection";
import { AssetUploadBlock } from "./AssetUploadBlock";
import { applyGroupChange, emptyGroup, groupCount, readGroup } from "./types";

type UploadAssetsModalProps = {
  isOpen: boolean;
  /** The brief working memory — groups are seeded from it; changes are sent as deltas. */
  memory: BriefMemory | null;
  onOpenChange: (isOpen: boolean) => void;
  resourceId?: string;
  threadId?: string;
};

export function UploadAssetsModal({
  isOpen,
  memory,
  onOpenChange,
  resourceId,
  threadId,
}: UploadAssetsModalProps) {
  // The thread id already encodes the request id (D2) — no new prop needs
  // to reach the modal from outside.
  const requestId = threadId ? requestIdFromSessionId(threadId) : null;

  const [assets, setAssets] = useState<AssetGroup>(emptyGroup);
  const [inspirations, setInspirations] = useState<AssetGroup>(emptyGroup);

  // Seed local state from the brief when the modal opens (not while editing, so
  // the live-save refetch doesn't clobber in-flight edits).
  useEffect(() => {
    if (!isOpen) return;

    setAssets(readGroup(memory?.assets));
    setInspirations(readGroup(memory?.inspirations));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const change = (
    target: MbcBriefAssetTarget,
    setGroup: (update: (group: AssetGroup) => AssetGroup) => void,
    delta: AssetGroupChange,
  ) => {
    setGroup((group) => applyGroupChange(group, delta));
    if (!threadId) return;
    void patchBrief(threadId, resourceId, {
      assetChanges: [{ target, ...delta }],
    });
  };

  const total = groupCount(assets) + groupCount(inspirations);

  return (
    <Modal
      classNames={{ modal: "w-[1068px] max-w-[calc(100vw-48px)]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog className="flex h-[784px] max-h-[90vh] flex-col">
        <div className="flex items-start gap-lg px-3xl pt-3xl">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border-secondary text-text-primary">
            <Upload className="size-5" />
          </span>
          <div className="flex flex-col gap-xxs">
            <h2 className="text-lg font-semibold text-text-primary">
              Upload assets
            </h2>
            <p className="text-sm text-text-tertiary">
              Add the files the designs should use, and anything they should
              draw inspiration from.
            </p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3xl py-xl">
          <div className="flex flex-col gap-lg">
            <AssetSection
              count={groupCount(assets)}
              icon={<PenTool className="size-5" />}
              subtitle="Logos, photos and other files to use in the designs."
              title="Assets"
            >
              <AssetUploadBlock
                onChange={(delta) => change("assets", setAssets, delta)}
                requestId={requestId}
                value={assets}
              />
            </AssetSection>

            <AssetSection
              count={groupCount(inspirations)}
              icon={<Camera className="size-5" />}
              subtitle="References the designs should draw from."
              title="Inspirations"
            >
              <AssetUploadBlock
                onChange={(delta) =>
                  change("inspirations", setInspirations, delta)
                }
                requestId={requestId}
                value={inspirations}
              />
            </AssetSection>
          </div>
        </div>

        <DialogFooter className="border-t border-border-secondary shrink-0">
          <Button onPress={() => onOpenChange(false)} variant="secondary">
            Cancel
          </Button>
          <Button
            isDisabled={total === 0}
            onPress={() => onOpenChange(false)}
            variant="primary"
          >
            Upload assets
            <Upload className="size-4" />
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
